import { observationPolicy } from "./realtime-observation";
import { Button, buttonVariants } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { clearPrivateQueries, historyQueryPolicy, privateQueryKey } from "./query-policy";
import { AccountQueryProvider } from "./account-query";
import type { TaskModelSummary } from "@nexra/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { api, dataApi } from "./api";
import { EmptyState, ErrorState, LoadingBlock, PageHeader, TaskPreview } from "./ui";
import { ModelIcon } from "./model-icon";
import { formatModelSlug } from "./model-family";
import { useTranslation } from "./i18n";
import { UserMenu } from "./components/ui/user-menu";
import { TopLoader } from "./components/ui/top-loader";
import { Brand } from "./components/ui/brand";
import { useSession, useModels, useAdminAccess, useSignOut, humanizeLabel, catalogSummary } from "./route-shared";

export function AppShell() {
  const { t } = useTranslation();
  const sessionQuery = useSession();
  const session = sessionQuery.data?.session ? sessionQuery.data : null;
  const adminQuery = useAdminAccess(session?.user?.id);
  const signOut = useSignOut();

  return (
    <div className="app-shell">
      <TopLoader />
      <header className="topbar">
        <Link className="inline-flex shrink-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring" to="/" aria-label={t("nav.homeAria")}>
          <Brand />
        </Link>
        <nav className="main-nav flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>a]:inline-flex [&>a]:h-9 [&>a]:shrink-0 [&>a]:items-center [&>a]:whitespace-nowrap [&>a]:rounded-md [&>a]:px-3 [&>a]:text-sm [&>a]:font-medium [&>a]:text-muted-foreground [&>a]:outline-none [&>a:hover]:bg-muted [&>a:hover]:text-foreground [&>a:focus-visible]:ring-2 [&>a:focus-visible]:ring-inset [&>a:focus-visible]:ring-ring [&>a[data-status=active]]:bg-accent [&>a[data-status=active]]:text-accent-foreground max-sm:[&>a]:px-2 max-sm:[&>a]:text-xs" aria-label={t("nav.mainAria")}>
          <Link to="/" activeOptions={{ exact: true }}>{t("nav.home")}</Link>
          <Link to="/sandbox">{t("nav.playground")}</Link>
          <Link to="/explore">{t("nav.models")}</Link>
          <Link to="/requests">{t("nav.requests")}</Link>
          <Link to="/docs">{t("nav.documentation")}</Link>
        </nav>
        <div className="topbar-actions">
          <UserMenu pending={sessionQuery.isLoading || signOut.isPending} isAdmin={!signOut.isPending && adminQuery.data === true} session={session} onSignOut={() => signOut.mutate()} />
        </div>
      </header>
      <main className="main-content">{signOut.isPending ? <LoadingBlock /> : <AccountQueryProvider userId={session?.user?.id}><Outlet key={session?.user?.id ?? "anonymous"} /></AccountQueryProvider>}</main>
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const sessionQuery = useSession();
  const modelsQuery = useModels();
  const [modelShelf, setModelShelf] = useState<"latest" | "recent">("latest");
  const [showGenerationPreviews, setShowGenerationPreviews] = useState(true);
  const tasksQuery = useQuery({
    ...historyQueryPolicy,
    queryKey: privateQueryKey("tasks", sessionQuery.data?.user?.id, { view: "dashboard", limit: 20 }),
    queryFn: ({ signal }) => dataApi.tasks(undefined, { limit: 20 }, signal),
    enabled: Boolean(sessionQuery.data?.session && (showGenerationPreviews || modelShelf === "recent")),
    retry: false,
    ...observationPolicy("history", 8_000),
  });
  const models = modelsQuery.data?.data ?? [];
  const tasks = tasksQuery.data?.data ?? [];
  const recentModels = useMemo(() => {
    const modelsBySlug = new Map(models.map((model) => [model.slug, model]));
    const seen = new Set<string>();
    return tasks.flatMap((task) => {
      if (seen.has(task.model)) return [];
      seen.add(task.model);
      const model = modelsBySlug.get(task.model);
      return model ? [model] : [];
    });
  }, [models, tasks]);

  return (
    <div className="page dashboard-page">
      <PageHeader
        title={t("dashboard.title")}
        description={sessionQuery.data?.user ? t("dashboard.welcomeBack", { name: sessionQuery.data.user.name || sessionQuery.data.user.email || "" }) : t("dashboard.guestWelcome")}
      />
      <div className="mb-4">
        <h2 className="m-0 text-lg font-semibold tracking-tight">{t("dashboard.quickStart")}</h2>
        <p className="mt-1 text-[13px] text-zinc-500">{t("dashboard.quickStartHint")}</p>
      </div>
      <section className="getting-started">
        <div className="getting-column">
          <div className="mb-3"><h3 className="m-0 text-[15px] font-semibold">{t("dashboard.createTitle")}</h3><p className="mt-1 text-[13px] text-zinc-500">{t("dashboard.createHint")}</p></div>
          <Link className="action-row" to="/sandbox"><span className="action-icon zinc">⌁</span><span><strong>{t("dashboard.sandboxTitle")}</strong><small>{t("dashboard.sandboxHint")}</small></span><b>→</b></Link>
          <Link className="action-row" to="/explore"><span className="action-icon blue">▦</span><span><strong>{t("dashboard.browseTitle")}</strong><small>{t("dashboard.browseHint")}</small></span><b>→</b></Link>
          {sessionQuery.data?.session ? <Link className="action-row" to="/requests"><span className="action-icon green">↺</span><span><strong>{t("dashboard.recentTitle")}</strong><small>{t("dashboard.recentHint")}</small></span><b>→</b></Link> : <Link className="action-row" to="/login"><span className="action-icon green">◎</span><span><strong>{t("dashboard.signInTitle")}</strong><small>{t("dashboard.signInHint")}</small></span><b>→</b></Link>}
        </div>
        <div className="getting-column">
          <div className="mb-3 flex items-start justify-between gap-4"><div><h3 className="m-0 text-[15px] font-semibold">{t("dashboard.exploreTitle")}</h3><p className="mt-1 text-[13px] text-zinc-500">{t("dashboard.exploreHint")}</p></div></div>
          {modelsQuery.isLoading ? <LoadingBlock /> : models.slice(0, 3).map((model) => (
            <Link className="latest-row" to="/models/$" params={{ _splat: model.slug }} key={model.id}>
              <ModelIcon size="md" source={[model.slug, model.displayName, model.family]} />
              <span><strong>{model.slug}</strong><small>{catalogSummary(model)}</small></span><b>→</b>
            </Link>
          ))}
          {!modelsQuery.isLoading && models.length === 0 ? <EmptyState title={t("dashboard.noPublished")} /> : null}
          <Link className="latest-row" to="/explore"><span className="modality-icon">▦</span><span><strong>{t("dashboard.browseTitle")}</strong><small>{t("dashboard.browseAllHint")}</small></span><b>→</b></Link>
        </div>
      </section>

      <Tabs className="mt-14 gap-4" value={modelShelf} onValueChange={value => setModelShelf(value as "latest" | "recent")}>
        <div className="flex flex-wrap items-center justify-between gap-x-4 border-b border-border">
          <TabsList variant="line" aria-label={t("dashboard.shelfAria")}>
            <TabsTrigger value="latest">{t("dashboard.latestModels")}</TabsTrigger>
            {sessionQuery.data?.session ? <TabsTrigger value="recent">{t("dashboard.recentlyUsed")}</TabsTrigger> : null}
          </TabsList>
          <Link className={buttonVariants({ variant: "outline", size: "sm", className: "my-1" })} to="/explore">{t("dashboard.viewAll")}</Link>
        </div>
        {(["latest", "recent"] as const).filter(shelf => shelf === "latest" || sessionQuery.data?.session).map(shelf => {
          const shelfModels = (shelf === "latest" ? models : recentModels).slice(0, 4);
          return <TabsContent key={shelf} value={shelf}>
            {modelsQuery.isLoading ? <LoadingBlock /> : modelsQuery.error ? <ErrorState error={modelsQuery.error} /> : (
              shelfModels.length > 0 ? <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">{shelfModels.map((model) => <DashboardModelTile key={model.id} model={model} />)}</div> : <EmptyState title={t("dashboard.noRecent")} detail={t("dashboard.noRecentDetail")} />
            )}
          </TabsContent>;
        })}
      </Tabs>

      {sessionQuery.data?.session ? (
        <section className="mt-14">
          <div className="section-heading"><div><h2>{t("dashboard.latestResults")}</h2><p>{t("dashboard.latestResultsHint")}</p></div><div className="flex items-center gap-4"><label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-600">{t("dashboard.showPreview")}<Input checked={showGenerationPreviews} onChange={(event) => setShowGenerationPreviews(event.target.checked)} type="checkbox" /></label><Link to="/requests">{t("dashboard.viewAll")}</Link></div></div>
          {!showGenerationPreviews ? <p className="text-xs text-zinc-500">{t("dashboard.previewHidden")}</p> : tasksQuery.isLoading ? <LoadingBlock /> : tasks.length > 0 ? (
            <div className="task-grid">{tasks.slice(0, 4).map((task) => <TaskPreview compact key={task.id} showPreview={showGenerationPreviews} task={task} />)}</div>
          ) : <EmptyState title={t("dashboard.noResults")} detail={t("dashboard.noResultsDetail")} />}
        </section>
      ) : null}
    </div>
  );
}

function DashboardModelTile({ model }: Readonly<{ model: TaskModelSummary }>) {
  const cleanId = formatModelSlug(model.slug);
  return (
    <Link className="group grid min-h-[98px] grid-cols-[82px_minmax(0,1fr)] overflow-hidden border border-zinc-200 bg-white transition hover:border-zinc-300 hover:shadow-sm" to="/models/$" params={{ _splat: model.slug }}>
      <span className={`model-market-hero modality-${model.modality} relative flex items-end overflow-hidden p-3 text-white`}><strong className="relative z-[1] text-lg uppercase">{model.modality.slice(0, 1)}</strong></span>
      <span className="flex min-w-0 flex-col justify-between p-3">
        <span className="min-w-0"><strong className="block truncate text-[13px] text-zinc-950">{cleanId}</strong><span className="block truncate text-xs text-zinc-400">{model.displayName}</span></span>
        <span className="flex items-center justify-between gap-2"><Badge className="truncate bg-zinc-100 text-zinc-800">{humanizeLabel(model.operation)}</Badge><span className="text-xs text-zinc-400 transition group-hover:translate-x-0.5">→</span></span>
      </span>
    </Link>
  );
}

export { LoginPage } from "./login-page";

export function NotFoundPage() {
  const { t } = useTranslation();
  return <div className="page"><EmptyState title={t("notFound.title")} detail={t("notFound.detail")} /><div className="center-actions"><Link className={buttonVariants({ variant: "default", className: "" })} to="/">{t("notFound.backHome")}</Link></div></div>;
}
